import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { aiFailedStateMessages } from '@atlaskit/editor-common/ai-messages';
import { Text } from '@atlaskit/primitives/compiled';

import { useContentComponentContext } from '../hooks/useContentComponentContext';
import type { StepName } from './analytics/analyticsFlowTypes';
import { FAILURE_REASON } from './errors';

export type ErrorInfo = {
	apiName?: string | undefined;
	errorContent?: string | undefined;
	errorKey?: string | undefined;
	failureReason?: FAILURE_REASON | undefined;
	statusCode?: number | undefined;
};

const knownErrorMessages: Record<FAILURE_REASON, MessageDescriptor> = {
	[FAILURE_REASON.UNKNOWN]: aiFailedStateMessages.documentInsertError,
	[FAILURE_REASON.TOKEN_LIMIT]: aiFailedStateMessages.tokenLimitExceeded,
	[FAILURE_REASON.INSERT_FAILURE]: aiFailedStateMessages.documentInsertError,
	[FAILURE_REASON.INPUT_TOO_SHORT]: aiFailedStateMessages.inputTooShortError,
	[FAILURE_REASON.HIPAA_CONTENT]: aiFailedStateMessages.hipaaContentError,
	[FAILURE_REASON.RATE_LIMITED]: aiFailedStateMessages.rateLimitEnforced,
	[FAILURE_REASON.AUP_VIOLATION]: aiFailedStateMessages.aupViolationMessage,
	[FAILURE_REASON.ELEVATE_DISABLED_GENERATE]: aiFailedStateMessages.elevateDisabledGenerateError,
	[FAILURE_REASON.INTERNAL_SERVER_ERROR]: aiFailedStateMessages.internalServerError,
	[FAILURE_REASON.API_FAIL]: aiFailedStateMessages.apiError,
	[FAILURE_REASON.ADF_STREAMING_ERROR]: aiFailedStateMessages.adfStreamingError,
	[FAILURE_REASON.NO_TEXTUAL_CONTENT]: aiFailedStateMessages.noTextualContentError,
	[FAILURE_REASON.INVALID_JSON]: aiFailedStateMessages.apiError,
};

const aifcKnownErrorMessages: Record<FAILURE_REASON, MessageDescriptor> = {
	[FAILURE_REASON.UNKNOWN]: aiFailedStateMessages.documentInsertError, // no longer used in aifc
	[FAILURE_REASON.TOKEN_LIMIT]: aiFailedStateMessages.aifcTokenLimitExceeded,
	[FAILURE_REASON.INSERT_FAILURE]: aiFailedStateMessages.documentInsertError, // no longer used in aifc
	[FAILURE_REASON.INPUT_TOO_SHORT]: aiFailedStateMessages.aifcInputTooShortError,
	[FAILURE_REASON.HIPAA_CONTENT]: aiFailedStateMessages.aifcHipaaContentError,
	[FAILURE_REASON.RATE_LIMITED]: aiFailedStateMessages.aifcRateLimitEnforced,
	[FAILURE_REASON.AUP_VIOLATION]: aiFailedStateMessages.aifcAupViolationMessage,
	[FAILURE_REASON.ELEVATE_DISABLED_GENERATE]: aiFailedStateMessages.elevateDisabledGenerateError, // not relevant to aifc
	[FAILURE_REASON.INTERNAL_SERVER_ERROR]: aiFailedStateMessages.aifcInternalServerError,
	[FAILURE_REASON.API_FAIL]: aiFailedStateMessages.aifcApiErrorRetry,
	[FAILURE_REASON.ADF_STREAMING_ERROR]: aiFailedStateMessages.aifcAdfStreamingError,
	[FAILURE_REASON.NO_TEXTUAL_CONTENT]: aiFailedStateMessages.noTextualContentError,
	[FAILURE_REASON.INVALID_JSON]: aiFailedStateMessages.aifcApiErrorRetry,
};

export const documentationUrls: { [key in FAILURE_REASON]?: string } = {
	[FAILURE_REASON.INTERNAL_SERVER_ERROR]: 'https://status.atlassian.com/',
	[FAILURE_REASON.AUP_VIOLATION]: 'https://www.atlassian.com/legal/acceptable-use-policy',
	[FAILURE_REASON.RATE_LIMITED]:
		'https://support.atlassian.com/organization-administration/docs/excessive-use-in-atlassian-intelligence/',
};

export const aifcDocumentationUrls: { [key in FAILURE_REASON]?: string } = {
	[FAILURE_REASON.INTERNAL_SERVER_ERROR]: 'https://rovo.status.atlassian.com/',
	[FAILURE_REASON.AUP_VIOLATION]: 'https://www.atlassian.com/legal/acceptable-use-policy', // no change
	[FAILURE_REASON.RATE_LIMITED]:
		'https://support.atlassian.com/organization-administration/docs/excessive-use-in-atlassian-intelligence/', // no change
};

const getErrorMessage = (error: FAILURE_REASON, isInDocumentStreamingEnabled?: boolean) => {
	if (isInDocumentStreamingEnabled) {
		return aifcKnownErrorMessages[error];
	}
	return knownErrorMessages[error];
};

// Use of links in messages is based on guidance at https://developer.atlassian.com/platform/localization/i18n-formatting/avoiding-concatenation/#links
const LinkedMessage = ({
	error,
	documentationUrl,
}: {
	documentationUrl: string | undefined;
	error: FAILURE_REASON;
}) => {
	const { isInDocumentStreamingEnabled } = useContentComponentContext();

	const errorMessage = getErrorMessage(error, isInDocumentStreamingEnabled);
	return (
		<FormattedMessage
			// Ignored via go/ees005
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...errorMessage}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			values={{
				link: (message: React.ReactNode[]) => (
					// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
					<a href={documentationUrl} target="_blank" rel="noopener noreferrer">
						{message}
					</a>
				),
			}}
		/>
	);
};

// 500 status codes should not be retryable
const is5xxErrorRange = (statusCode: number) => statusCode >= 500 && statusCode < 600;

export const retryEnabled = ({ failureReason, statusCode = 500 }: ErrorInfo): boolean =>
	failureReason === FAILURE_REASON.ADF_STREAMING_ERROR ||
	failureReason === FAILURE_REASON.INVALID_JSON ||
	(failureReason === FAILURE_REASON.API_FAIL && !is5xxErrorRange(statusCode));

export const getStepName = ({ apiName, failureReason }: ErrorInfo): StepName => {
	return apiName === 'assistance-service' ||
		failureReason === FAILURE_REASON.AUP_VIOLATION ||
		failureReason === FAILURE_REASON.API_FAIL
		? 'apiError'
		: 'failed';
};

export const ErrorMessage = ({ errorKey, failureReason, statusCode }: ErrorInfo): JSX.Element => {
	const { formatMessage } = useIntl();
	const { isInDocumentStreamingEnabled } = useContentComponentContext();

	const isConvoAIServerError = [
		'FEATURE_DISABLED',
		'FEATURE_DISABLED_ON_SITE',
		'NO_AGENT',
	].includes(errorKey || '');

	// 9001 is a special case for elevate where we disable generate until Q3
	if (statusCode === 9001) {
		return <Text>{formatMessage(knownErrorMessages['elevate disabled generate'])}</Text>;
	}

	if (
		(statusCode && is5xxErrorRange(statusCode)) ||
		isConvoAIServerError ||
		failureReason === FAILURE_REASON.INTERNAL_SERVER_ERROR
	) {
		return (
			<LinkedMessage
				error={FAILURE_REASON.INTERNAL_SERVER_ERROR}
				documentationUrl={
					(isInDocumentStreamingEnabled ? aifcDocumentationUrls : documentationUrls)[
						FAILURE_REASON.INTERNAL_SERVER_ERROR
					]
				}
			/>
		);
	}

	if (!failureReason) {
		return <Text>{formatMessage(knownErrorMessages['unknown fail'])}</Text>;
	}

	if (failureReason === FAILURE_REASON.AUP_VIOLATION) {
		return (
			<LinkedMessage
				error={FAILURE_REASON.AUP_VIOLATION}
				documentationUrl={
					(isInDocumentStreamingEnabled ? aifcDocumentationUrls : documentationUrls)[
						FAILURE_REASON.AUP_VIOLATION
					]
				}
			/>
		);
	}

	if (failureReason === FAILURE_REASON.RATE_LIMITED) {
		return (
			<LinkedMessage
				error={FAILURE_REASON.RATE_LIMITED}
				documentationUrl={
					(isInDocumentStreamingEnabled ? aifcDocumentationUrls : documentationUrls)[
						FAILURE_REASON.RATE_LIMITED
					]
				}
			/>
		);
	}

	return <Text>{formatMessage(getErrorMessage(failureReason, isInDocumentStreamingEnabled))}</Text>;
};
